import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  signal,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { Observable, Subject, Subscription } from 'rxjs';
import { bufferTime, debounceTime, delay, filter, map, take, tap } from 'rxjs/operators';
import moment from 'moment';
import {
  cloneDeep,
  filter as _filter,
  forEach,
  has,
  isEmpty,
  isEqual,
  isNaN,
  isNil,
  keys,
  mapValues,
  pick,
  reduce,
  some,
  sortBy,
  uniqueId,
} from 'lodash';

import {
  CopyPasteState,
  DynamicFormData,
  FieldConfig,
  FormConfig,
  FormFieldConfig,
} from '@app/shared/forms/dynamic-form/fields.service';
import { Field, FieldType } from '@app/origami-api/constants';
import { DynamicFieldsService } from '@app/shared/forms/dynamic-form/dynamic-fields/dynamic-fields.service';
import { StringHash } from '@app/shared/utils/utils-types';
import { Converter } from '@app/shared/utils/converter';
import { SimpleTopic, TimeZoneInfo, TopicField } from '@app/origami-api/utils/topic-utils';
import { RelatedFieldsService } from '@app/services/related-fields.service';
import { FormInputBaseComponent } from '@app/shared/forms/dynamic-form/dynamic-fields/form-input-base/form-input-base.component';
import { AdvancedValidatorListItem } from '@app/shared/forms/dynamic-form/advanced-validator-list/advanced-validator-list.component';
import { TimeZoneService } from '@app/services/time-zone.service';
import { SessionService } from '@app/session/session.service';
import { SiteServiceModel } from '@app/origami-api/services/site.service';

import { ComponentRendererComponent } from '@app/shared/ui/simple-components/component-renderer/component-renderer.component';
import { LayoutSection } from '@app/pages/main/pages/quality/pages/quality-settings/pages/form-setting/components/form-layout-editor-modal/components/form-layout-editor/models';
import { ConfigServiceModel } from '@app/origami-api/services/config.service';
import { DynamicFormSectionComponent } from '@app/shared/forms/dynamic-form/dynamic-form/dynamic-form-section/dynamic-form-section.component';
import TopicFields = Converter.TopicFields;
import FormSectionAppearance = ConfigServiceModel.FormSectionAppearance;
import { getValidatorFn } from '@app/shared/forms/dynamic-form/dynamic-form.utils';

@Component({
  standalone: false,
  selector: 'ti-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('container') public container: ElementRef;
  @ViewChildren('renderer') renderers: QueryList<any>;
  @ViewChildren(ComponentRendererComponent) rendererComponentsList: QueryList<ComponentRendererComponent<any, any>>;
  @ViewChildren(DynamicFormSectionComponent) sectionComponents: QueryList<DynamicFormSectionComponent>;

  @Input() formConfig: FormConfig;
  @Input() useFieldsCheckers: boolean = false;
  @Input() useFormIdForCheckerIndex: boolean = true;
  @Input() renderBooleansAsToggles: boolean = false;
  @Input() fieldsProcessor: (fieldConfig: FormFieldConfig, values: any) => FormFieldConfig = (fieldConfig) =>
    fieldConfig;
  @Input() fieldsFilter: (fieldConfig: FormFieldConfig) => boolean = (fieldConfig) =>
    Field.Is.includeInCoding(fieldConfig);

  @Input() dynamicFieldsService: DynamicFieldsService; // = new DynamicFieldsService(); // will be overridden; this is a fallback solution
  //@todo think how handle it for old forms.
  @Input()
  public _isWide: boolean;
  @Input() isStrictCheckReadOnly: boolean = true;
  @Input() isWide: boolean;
  @Input() isReadOnly: boolean;
  @Input() isStandalone: boolean; // used for sections
  @Input() isFieldRemovable: boolean;
  @Input() ignoreFormRebuildRequest: boolean = false;
  @Input() monitorResize: boolean = true;
  @Input() isNewEntity: boolean;
  @Input() isDraftEntity: boolean = false;
  @Input() isAlreadyEffectiveDocument: boolean;
  @Input() isChangeControlOptionsEnabled: boolean;
  @Input() isCreateDocument: boolean;
  @Input() isEditMode: boolean;
  @Input() hasESignatureRequest: boolean;
  @Input() isPlaceholder: boolean;
  @Input() viewMode: number;
  @Input() entity: SimpleTopic;
  @Input() isPanelbarView: boolean = false;
  @Input() isPanelbarItemsExpanded: boolean = false;
  @Input() formSeed: string; //id
  @Input() fieldCustomChangeRequest: EventEmitter<[string, string, any]> = new EventEmitter<[string, string, any]>();
  @Input() roomId: number;
  @Input() isFacetsForm: boolean = false;
  @Input() forceEmmitValue: boolean = false;
  @Input() isLegacySectionMode: boolean = true;
  @Input() controlsAppendTo: 'root' | 'component' = 'component';

  @Output() onChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() onFormReady: EventEmitter<any> = new EventEmitter<any>();
  @Output() onFormGenerated: EventEmitter<any> = new EventEmitter<any>();
  @Output() onFormDestroy: EventEmitter<any> = new EventEmitter<any>();
  @Output() onFormResize: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onFieldRemoveRequest: EventEmitter<string> = new EventEmitter<string>();

  public fieldsMap: Record<string, FormFieldConfig> = {};
  public sections: LayoutSection[] = [];
  public sectionState: Record<string, boolean> = {};
  private prevId: number;
  public visible: boolean;

  selected: { [key: string]: boolean } = {};

  readonly strings = tiStrings.shared.dynamicForms.dynamicForms;

  private onReadySubject = new Subject<any>();
  public seed: string;
  public validationErrors: AdvancedValidatorListItem[] = [];
  @Input() showValidationErrors: boolean;
  private _section;
  public get section() {
    if (!this._section) {
      this._section = this.container.nativeElement.closest('section'); // ugly solution
    }

    return this._section;
  }
  public get tooltipRight() {
    const viewportRect = document.body.getBoundingClientRect();
    return (
      viewportRect.width - (this.section.getBoundingClientRect().left + this.section.getBoundingClientRect().width)
    );
  }
  public get tooltipBottom() {
    const viewportRect = document.body.getBoundingClientRect();
    return (
      viewportRect.height - (this.section.getBoundingClientRect().top + this.section.getBoundingClientRect().height)
    );
  }

  private _isLinkedRoomMetadataMode: boolean;
  @Input() set isLinkedRoomMetadataMode(v: boolean) {
    if (this._isLinkedRoomMetadataMode !== v) {
      this.needFormRebuild = true;
    }
    this._isLinkedRoomMetadataMode = v;
  }
  get isLinkedRoomMetadataMode() {
    return this._isLinkedRoomMetadataMode;
  }

  @Input() explicitFields: FormFieldConfig[];

  @Input() copyPasteState: CopyPasteState = {
    visibility: { value: signal(false) },
    change: signal(false),
    copiedFields: {},
  };

  public readonly criticalWidth: number = 450;

  public fieldsToGenerate: FormFieldConfig[];

  public collectionMap;

  public formGroup: UntypedFormGroup;
  private formGroupChange$: Subscription;
  private formGroupSpecialFieldsChange$: Subscription;
  private specialFieldsChangeCache: StringHash<any>;

  private subs: Subscription = new Subscription();

  public value: StringHash<any> = {};

  private needFormRebuild: boolean;

  private isTransformingForm: boolean;

  public isLoading = false;

  public _data: DynamicFormData;

  private relatedFieldsDisplayValues: StringHash<any> = {};

  private dateTimeFields: string[] = [];

  generateFormTimeout: number;

  @Input() set data(d: DynamicFormData) {
    this._data = d;
    this._data?.fields.map((field) => {
      if (typeof field.value === 'string') {
        field.value = field.value?.trim();
      }
    });
    if (!d || (!d.fields && !d.isCreation)) {
      this.cleanupForm();
      return;
    }

    if (!d.form && !this.formConfig) {
      this.cleanupForm();
      this.onFormReady.emit();
      return;
    }

    if (this.needFormRebuild) {
      this.cleanupForm();
    }

    if (d.form) {
      // let's override initial form with the form that is specific for the loaded entity
      this.formConfig = d.form;
    }

    let values = this.getValuesHash(d.fields);

    if (this.generateFormTimeout) {
      clearTimeout(this.generateFormTimeout);
      this.generateFormTimeout = null;
    }

    if (!this.formGroup) {
      this.formGroup = this.formBuilder.group({});
    }

    this.generateFormTimeout = window.setTimeout(() => {
      // need to initialize all inputs
      this.generateForm(values, d.needCustomForm, false, !!this.isNewEntity)
        .pipe(
          take(1),
          debounceTime(10), // TODO: check this
        )
        .subscribe(() => {
          setTimeout(() => {
            // need to wait until the form is ready
            this.setValue(values);

            this.initEvents();
            this.onFormReady.emit();
            this.onReadySubject.next(this._data.fields);
            this.onFormGenerated.emit();
            this.cdr.detectChanges();
          }, 0);
        });
    });
  }

  private formRebuildRequestEmitter = new EventEmitter();
  private formRebuildRequestObs = this.formRebuildRequestEmitter.pipe(debounceTime(100));

  @Input() relatedDynamicForms: DynamicFormComponent[];
  @Input() roomInfo: SiteServiceModel.LoginToSiteResult;

  public get componentHash(): StringHash<FormInputBaseComponent> {
    return this.componentsHash;
  }
  private componentsHash: StringHash<FormInputBaseComponent>;
  private isRoomLevel: boolean;
  private roomTimeZone: TimeZoneInfo;

  fieldsIncludedInCoding: FormFieldConfig[];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private relatedFieldsService: RelatedFieldsService,
    protected sessionService: SessionService,
    protected timeZoneService: TimeZoneService,
  ) {
    this.seed = uniqueId('df-');
    this.isRoomLevel = Boolean(this.sessionService.room?.id);
  }

  ngOnInit(): void {
    this.fieldsIncludedInCoding = this.formConfig?.formFields?.filter((field) => Field.Is.includeInCoding(field));
    //this.initEvents();
    this.dynamicFieldsService.setRoomId(this.roomId);
    if (this.formSeed && this.fieldCustomChangeRequest && this.dynamicFieldsService) {
      this.dynamicFieldsService.fieldCustomChangeRequests[this.formSeed] = this.fieldCustomChangeRequest;
    }
    this.subs.add(this.onFormReady.pipe(delay(100)).subscribe(() => this.applyConditionalValidations()));
    this.subs.add(
      this.formRebuildRequestObs.subscribe(() => {
        this.rebuildForm(true);
      }),
    );
    this.roomTimeZone = this.roomInfo?.RoomTimeZone || this.sessionService.room?.timeZone;
  }

  ngOnDestroy(): void {
    this.onFormDestroy.emit();
    this.formGroupChange$ && this.formGroupChange$.unsubscribe();
    this.formGroupSpecialFieldsChange$ && this.formGroupSpecialFieldsChange$.unsubscribe();
    this.subs.unsubscribe();
  }

  public generateForm(
    values?: StringHash<any>,
    needCustomForm?: boolean,
    needDisplayValuesFiltering?: boolean,
    isCreation?: boolean,
  ): Observable<any> {
    this.isLoading = true;

    this.cdr.detectChanges();

    this.needFormRebuild = false;

    const relatedFields = this.relatedFieldsService.getRelatedFieldsFromFormConfig(this.formConfig);
    const fieldNamesToFilterDisplayValues = this.dynamicFieldsService.getFieldNamesToFilterDisplayValues();
    const displayValues = this.dynamicFieldsService.getCustomDisplayValues(values);

    this.formConfig.formFields.forEach((field) => {
      if (Field.Is.useTimePart(field.flags)) {
        this.dateTimeFields.push(field.fieldName);
      }
    });

    return this.dynamicFieldsService
      .prepareForm(
        this.formConfig,
        values || this.value,
        needCustomForm,
        this.isLinkedRoomMetadataMode,
        null,
        isCreation,
      )
      .pipe(
        take(1),
        tap((form: FormConfig) => {
          if (!this.formGroup) {
            this.formGroup = this.formBuilder.group({});
          }
          form.formFields = this.getMergedFieldsConfig(form.formFields);
          this.formConfig = form;

          const formFieldsWithDefaultValues = this.formConfig.formFields.filter((ff) => ff.fieldValue !== undefined);

          this.dynamicFieldsService.setPlaceholder(this.isPlaceholder);

          if (this.dynamicFieldsService.checkFieldCleanupNeeded()) {
            Object.keys(this.formGroup.controls).forEach((key: string) => {
              this.formGroup.removeControl(key);
            });
          }

          this.componentsHash = {};

          this.fieldsToGenerate = this.formConfig.formFields
            .map((formField) => {
              if (this.fieldsProcessor) {
                return this.fieldsProcessor(formField, this.formGroup.value);
              }
              return formField;
            })
            .filter(this.fieldsFilter || ((fieldConfig: FormFieldConfig) => Field.Is.includeInCoding(fieldConfig)))
            .sort((a: FormFieldConfig, b: FormFieldConfig) => {
              if (this.isFacetsForm) {
                return a.facetSortOrder - b.facetSortOrder;
              } else {
                return a.sortOrder - b.sortOrder;
              }
            })
            .map((fieldConfig: FormFieldConfig) => {
              fieldConfig.UIData = fieldConfig.UIData || {};
              fieldConfig.UIData.isWide = this.isWide === undefined ? this._isWide : this.isWide;
              fieldConfig.UIData.isLinkedRoomMetadataMode = this.isLinkedRoomMetadataMode;
              fieldConfig.UIData.copyPasteState = this.copyPasteState;
              fieldConfig.UIData.isNewEntity = this.isNewEntity;
              fieldConfig.UIData.isDraftEntity = this.isDraftEntity;
              fieldConfig.UIData.appendTo = this.controlsAppendTo;
              fieldConfig.width = fieldConfig.width ?? 100;

              if (this.renderBooleansAsToggles) {
                if (fieldConfig.customFieldSettings) {
                  fieldConfig.customFieldSettings.renderBooleansAsToggles = true;
                } else {
                  fieldConfig.customFieldSettings = {
                    renderBooleansAsToggles: true,
                  };
                }
              }

              let initialValue;

              if (values && values[fieldConfig.fieldName] !== undefined) {
                initialValue = values[fieldConfig.fieldName];
              }

              if (initialValue !== null && initialValue !== undefined) {
                fieldConfig.UIData.hasActualDbValue = true;
              }

              if (isCreation && formFieldsWithDefaultValues && formFieldsWithDefaultValues.length) {
                const formFieldWithDefaultValue = formFieldsWithDefaultValues.find(
                  (ff) => ff.fieldName === fieldConfig.fieldName,
                );
                const defaultValue = formFieldWithDefaultValue
                  ? TopicFields.getPreparedFieldValueUI(
                      formFieldWithDefaultValue.fieldValue,
                      formFieldWithDefaultValue.fieldType,
                    )
                  : null;

                if (defaultValue !== null && isNil(initialValue)) {
                  initialValue = defaultValue;
                }

                if (
                  defaultValue === initialValue ||
                  (defaultValue && initialValue && defaultValue.toString() === initialValue.toString())
                ) {
                  fieldConfig.UIData.defaultValueFromCustomForm =
                    defaultValue !== null &&
                    defaultValue !== undefined &&
                    defaultValue !== '' &&
                    !this.sessionService.room?.settings?.documentTypes.common.allowModifyDefaultValues.get();
                  values[fieldConfig.fieldName] = initialValue;
                  if (this.useFieldsCheckers) {
                    this.selected[this.getFieldCheckerIndex(fieldConfig)] = true;
                  }
                }
              }

              const field = this.dynamicFieldsService.registerField(
                this.formGroup,
                this.formBuilder,
                fieldConfig,
                (fc) => Field.Is.readOnly(fc.flags),
                (fc) => getValidatorFn(fc, this.timeZoneService),
                initialValue,
                (item) => (fieldConfig.UIData.valueChange ? fieldConfig.UIData.valueChange(item) : null),
                this.onReadySubject,
                this.entity,
                this.formSeed,
                this.formConfig,
              );

              const oldUpdater = field.renderer.updater;
              field.renderer.updater = (instance: FormInputBaseComponent, data) => {
                const oldUnsubs = oldUpdater.call(field.renderer, instance, data);
                const subs = new Subscription();

                if (this.formGroup && this.formGroup.controls && this.formGroup.controls[fieldConfig.fieldName]) {
                  if (!this.ignoreFormRebuildRequest) {
                    subs.add(
                      instance.formRebuildRequestEmitter.subscribe(() => {
                        this.formRebuildRequestEmitter.emit();
                      }),
                    );
                  }

                  if (this.useFieldsCheckers) {
                    subs.add(
                      this.formGroup.controls[fieldConfig.fieldName].valueChanges.subscribe((value) => {
                        this.selected[this.getFieldCheckerIndex(field)] =
                          value !== null && value !== undefined && value !== '';
                        this.onChange.emit({ fieldConfig: field, renderers: this.renderers, value: value });
                      }),
                    );

                    if (values[fieldConfig.fieldName]) {
                      this.selected[this.getFieldCheckerIndex(field)] = true;
                    }
                  } else {
                    subs.add(
                      this.formGroup.controls[fieldConfig.fieldName].valueChanges.subscribe((value) => {
                        this.onChange.emit({ fieldConfig: field, renderers: this.renderers, value: value });
                      }),
                    );
                  }
                }

                if (
                  values &&
                  (!needDisplayValuesFiltering ||
                    !fieldNamesToFilterDisplayValues ||
                    !fieldNamesToFilterDisplayValues.includes(instance.config.fieldName))
                ) {
                  if (displayValues[instance.config.fieldName]) {
                    instance.displayValue = displayValues[instance.config.fieldName];
                  } else if (relatedFields[instance.config.fieldName]) {
                    instance.displayValue = values[relatedFields[instance.config.fieldName]];
                  } else {
                    instance.displayValue = null;
                  }
                }

                if (
                  fieldNamesToFilterDisplayValues &&
                  fieldNamesToFilterDisplayValues.includes(instance.config.fieldName) &&
                  relatedFields[instance.config.fieldName]
                ) {
                  instance.valueChangeEmitter.subscribe((r) => {
                    this.relatedFieldsDisplayValues[relatedFields[instance.config.fieldName]] = instance.displayValue;
                  });
                }

                if (Field.Is.readOnly(fieldConfig) || this.isReadOnly) {
                  instance.forceDisabled = true;
                  instance.self && instance.self.disable();
                } else {
                  instance.forceDisabled = false;
                  instance.self && instance.self.enable();
                }

                instance.isReadonly = this.isReadOnly;

                this.componentsHash[fieldConfig.fieldName] = instance;

                return () => {
                  oldUnsubs && oldUnsubs();
                  subs && subs.unsubscribe();
                };
              };

              return field;
            });
          this.resolveSiblingDateBoundaryTitles();
          this.resetHiddenFieldsValidators();
          if (!this.isLegacySectionMode && this.formConfig.sections?.length > 0) {
            this.sections = sortBy(this.formConfig.sections, 'sortOrder');
            this.collectionMap = this.formConfig.collectionMap;
            this.fieldsMap = {};

            this.fieldsToGenerate.forEach((field) => {
              this.fieldsMap[field.fieldName] = field;

              if (!this.collectionMap[field.parent] && Field.Is.includeInCoding(field)) {
                //    console.warn('field without parent ', field);
              }
            });

            this.collectionMap = mapValues(this.collectionMap, (list) =>
              _filter(sortBy(list, 'sortOrder'), (item) => item.type === 'group' || has(this.fieldsMap, item.name)),
            );

            this.sections = this.sections.filter((section) => {
              let sectionFields = [];

              forEach(this.collectionMap[section.name], (item) => {
                if (item.type === 'field') {
                  sectionFields.push(this.fieldsMap[item.name]);
                  return;
                }

                if (item.type === 'group') {
                  sectionFields = sectionFields.concat(this.collectionMap[item.name]);
                }
              });

              return sectionFields.length > 0;
            });

            const newState = reduce(
              this.sections,
              (acc, section) => ({
                ...acc,
                [section.name]: section.collapsed,
              }),
              {},
            );

            if (this.prevId !== this.entity?.TopicId || !isEqual(keys(newState), keys(this.sectionState))) {
              this.sectionState = newState;
            }

            this.isLegacySectionMode = this.sections.length == 0;

            // console.log(
            //   'Dynamic form result',
            //   this.sections,
            //   this.collectionMap,
            //   this.fieldsMap,
            //   this.fieldsToGenerate,
            // );
          } else {
            this.isLegacySectionMode = true;
          }

          this.prevId = this.entity?.TopicId;

          if (this.forceEmmitValue) {
            this.onChange.emit({ value: this.formGroup.getRawValue(), fieldConfig: { fieldName: null } });
          }
        }),
        tap(() => {
          this.isLoading = false;
        }),
      );
  }

  private getMergedFieldsConfig(formFields?: FormFieldConfig[]): FormFieldConfig[] {
    formFields = formFields || this.formConfig?.formFields || [];

    if (!this.explicitFields || !this.explicitFields.length) {
      return formFields;
    }

    const fieldsToAppend: FormFieldConfig[] = [];

    this.explicitFields.forEach((ef: FormFieldConfig) => {
      let idx = formFields.findIndex((ff) => ff.fieldName === ef.fieldName);
      // we need clone in order to recreate these fields properly
      if (idx >= 0) {
        formFields[idx] = cloneDeep(ef);
      } else {
        fieldsToAppend.push(cloneDeep(ef));
      }
    });

    return [...formFields, ...fieldsToAppend];
  }

  private initEvents() {
    this.formGroupChange$ && this.formGroupChange$.unsubscribe();
    this.formGroupChange$ = this.formGroup.valueChanges
      .pipe(
        filter(() => !this.isTransformingForm),
        debounceTime(100),
      )
      .subscribe((values) => {
        this.onFormChange(values);
      });

    this.formGroupSpecialFieldsChange$ && this.formGroupSpecialFieldsChange$.unsubscribe();
    this.formGroupSpecialFieldsChange$ = this.dynamicFieldsService
      .getOnSpecialFieldsChangeSubscription(this.formGroup)
      .pipe(
        bufferTime(100),
        map((values) => reduce(values, (result, item) => ({ ...result, ...item }), {})),
        filter((value) => !isEmpty(value)),
      )
      .subscribe((v: StringHash<any>) => {
        this.value = { ...this.value, ...v };

        this.needFormRebuild = this.dynamicFieldsService.shouldRebuildForm(v, this.specialFieldsChangeCache);
        this.specialFieldsChangeCache = v;
      });
  }

  public cleanupForm() {
    if (!this.formGroup) {
      return; // nothing to do
    }

    this.formGroup.reset();

    Object.keys(this.formGroup.controls).forEach((key: string) => {
      this.formGroup.removeControl(key);
    });

    this.formGroup = null;

    this.fieldsToGenerate = null;

    this.cdr.detectChanges();
  }

  public rebuildForm(needDisplayValuesFiltering?: boolean, needFormRebuild = true) {
    this.needFormRebuild = needFormRebuild;
    this.onFormChange(this.formGroup?.value, needDisplayValuesFiltering, needFormRebuild);
  }

  /*
  public prepareAndSetValues(values: StringHash<any>): void {
    if (!values) {
      return;
    }

    const preparedValues = this.getValuesHash(
      this.formConfig.formFields
        .map((fieldConfig: FormFieldConfig): FieldConfig => {
          const value = values.hasOwnProperty(fieldConfig.fieldName) ? values[fieldConfig.fieldName] : fieldConfig.fieldValue;

          return value ? {
            value: value,
            name: fieldConfig.fieldName,
            type: fieldConfig.fieldType,
            title: fieldConfig.fieldTitle
          } : null;
        })
        .filter(config => config)
    );

    this.setValue(preparedValues);
  }
  */

  public setValue(values: StringHash<any>, saveValues: boolean = false, preserveExistingValues = false): void {
    if (!values) {
      return;
    }

    if (preserveExistingValues) {
      Object.keys(this.value).forEach((key) => {
        if (isNil(this.value[key])) {
          return;
        }

        delete values[key];
      });
    }

    this.value = values;

    if (saveValues) {
      this._data.fields = this.formConfig.formFields
        .map((fieldConfig: FormFieldConfig): FieldConfig => {
          const value = values.hasOwnProperty(fieldConfig.fieldName)
            ? values[fieldConfig.fieldName]
            : fieldConfig.fieldValue;

          return value
            ? {
                value,
                name: fieldConfig.fieldName,
                type: fieldConfig.fieldType,
                title: fieldConfig.fieldTitle,
              }
            : null;
        })
        .filter((config) => config);
    }

    if (this.formGroup) {
      this.formGroup.patchValue(
        mapValues(values, (v) => (v === undefined ? null : v)),
        { emitEvent: true },
      );
    }
  }

  public getSingleValue(fieldName: string) {
    return this.value[fieldName];
  }

  public getValue(
    filterByReadOnly: boolean = true,
    filterByEmptyValue: boolean = false,
    filterByVirtual: boolean = true,
  ): FieldConfig[] {
    return this.getMergedFieldsConfig()
      .filter((fieldConfig: FormFieldConfig) => {
        // TODO: refactor all that
        if (
          (filterByVirtual && Field.Is.virtual(fieldConfig)) ||
          (filterByReadOnly && Field.Is.readOnly(fieldConfig))
        ) {
          return false;
        }

        if (this.useFieldsCheckers) {
          // we should allow null values for checked fields
          if (this.value[fieldConfig.fieldName] === undefined) {
            // workaround for the masscoding of boolean fields
            if (fieldConfig.fieldType === FieldType.Boolean) {
              this.value[fieldConfig.fieldName] = false;
            } else {
              this.value[fieldConfig.fieldName] = null;
            }
          }

          return this.fieldsFilter(fieldConfig) && this.selected[this.getFieldCheckerIndex(fieldConfig)];
        }

        //TODO: refactor this
        return (
          this.fieldsFilter(fieldConfig) &&
          (filterByEmptyValue ||
            this.value[fieldConfig.fieldName] !== undefined ||
            this.value[fieldConfig.fieldName] !== null ||
            fieldConfig.fieldType === FieldType.Number)
        );
      })
      .map((fieldConfig: FormFieldConfig) => {
        let value = this.value[fieldConfig.fieldName];
        const applyTimeZone = Field.Is.applyTimeZone(fieldConfig.flags);

        if (fieldConfig.fieldType === FieldType.Date && this.roomTimeZone && applyTimeZone) {
          value =
            typeof value === 'object' && (value?.start || value?.end)
              ? value
              : this.timeZoneService.convertRoomDateToDomainDate(value, false, this.roomTimeZone);
        }

        return {
          name: fieldConfig.fieldName,
          title: fieldConfig.fieldTitle,
          type: fieldConfig.fieldType,
          value: Converter.TopicFields.getPreparedFieldValueDB(value, fieldConfig.fieldType),
          applyTimeZone,
        } as FieldConfig;
      });
  }

  public getRelatedFieldsValue(): TopicField[] {
    return this.getMergedFieldsConfig()
      .filter((fieldConfig: FormFieldConfig) =>
        Object.prototype.hasOwnProperty.call(this.relatedFieldsDisplayValues, fieldConfig.fieldName),
      )
      .map((fieldConfig: FormFieldConfig) => {
        return {
          FieldName: fieldConfig.fieldName,
          FieldType: fieldConfig.fieldType,
          Fieldvalue: Converter.TopicFields.getPreparedFieldValueDB(
            this.relatedFieldsDisplayValues[fieldConfig.fieldName],
            fieldConfig.fieldType,
          ),
        } as TopicField;
      });
  }

  private getValuesHash(values: FieldConfig[]): StringHash<any> {
    const valuesObj = {};

    values.forEach((f: FieldConfig) => {
      valuesObj[f.name] = null;
    });

    const arrayToObject = (arr: FieldConfig[], keyField: string) =>
      Object.assign(
        valuesObj,
        ...arr.map((item) => {
          const isDateIntervalType =
            item.type === FieldType.Date && typeof item.value === 'object' && (item.value?.start || item.value?.end);

          const field = this.formConfig.formFields.find((formField) => formField?.fieldName === item.name); // for a weird reason there may be empty items in the fields list
          let result = isDateIntervalType
            ? item.value
            : Converter.TopicFields.getPreparedFieldValueUI(item.value, item.type);

          if (this.isRoomLevel && Field.Is.applyTimeZone(field) && !isDateIntervalType && result instanceof Date) {
            result = this.timeZoneService.convertServerDateToRoomDate(result);
          }
          return {
            [item[keyField]]: result,
          };
        }),
      );

    return arrayToObject(
      values.filter((item) => valuesObj[item.name] === null),
      'name',
    );
  }

  private onFormChange(values?: StringHash<any>, needDisplayValuesFiltering?: boolean, force: boolean = false) {
    const currentValues = pick(this.value, ...keys(values));

    if (!values) {
      return; // event came from the form clearing
    }
    values = values || (this.formGroup ? this.formGroup.value : {});
    if (isEqual(currentValues, values) && !force) {
      this.applyConditionalValidations(); // least dangerous fix?
      return;
    }
    Object.assign(this.value, values);

    if (this.needFormRebuild) {
      this.removeDefaultValues();
      this.generateForm(this.value, true, needDisplayValuesFiltering, !!this.isNewEntity)
        .pipe(take(1), debounceTime(10))
        .subscribe(() => {
          this.initEvents();
          this.onFormReady.emit(this.value);
          this.onReadySubject.next(this._data.fields);

          this.applyConditionalValidations();
          this.cdr.detectChanges();
        });
    } else {
      this.applyConditionalValidations();
    }
  }

  private removeDefaultValues() {
    const fieldsWithDefaultValues = this.formConfig.formFields.filter((ff) => !!ff.fieldValue);

    fieldsWithDefaultValues.forEach((ff) => {
      let value = this.value[ff.fieldName];

      switch (ff.fieldType) {
        case FieldType.Number:
          value = isNaN(+value) ? null : `${value}`;
          break;
        case FieldType.Boolean:
          value = !!+value;
          break;
        case FieldType.Date:
          value = value ? moment(value).format('MM/DD/YYYY') : value;
          break;
      }

      if (value === ff.fieldValue) {
        this.value[ff.fieldName] = undefined;
      }
    });
  }

  /*
  public trackByFn(index: number, item: FormFieldConfig): string {
    return `${item.fieldName}_${this.isLinkedRoomMetadataMode}`;
  }
  */

  /*
  private convertToFormModel(values: StringHash<any>) {
    values = values || this.value;

    let model = <StringHash<any>>{};

    Object.keys(this.formGroup.controls).forEach((fieldName) => {
      model[fieldName] = (values[fieldName] === undefined) ? null : this.value[fieldName];
    });

    return model;
  }
  */

  public getTopicFields(filterByReadOnly: boolean = true, filterByVirtual?: boolean): TopicField[] {
    return this.getAllTopicFields(filterByReadOnly, false, filterByVirtual).filter((item) => {
      const oldField = this._data.fields.find((field) => field.name === item.FieldName);
      //optional solution, risky, should be reworked later
      if (item && oldField?.type === FieldType.Date && (item.TimeZoneShift || item.TimeZoneShift2)) {
        let format = this.dateTimeFields.includes(oldField.name)
          ? Converter.DateAndTime.FIELDS_DATETIME_FORMAT
          : Converter.DateAndTime.FIELDS_DATE_FORMAT;
        if (oldField.value) {
          if (this.isRoomLevel) {
            format = Converter.DateAndTime.FIELDS_DATETIME_FORMAT;
          }
          if (oldField.value.indexOf('/') !== -1) {
            oldField.value = moment(oldField.value, Converter.DateAndTime.FIELDS_DATE_FORMAT).format(format);
          } else {
            oldField.value = moment(oldField.value).format(format);
          }
        }
      }
      return !oldField || !(oldField.value === item.Fieldvalue) || this.isNewEntity;
    });
  }

  public getAllTopicFields(
    filterByReadOnly: boolean = true,
    filterByEmptyValue: boolean = false,
    filterByVirtual?: boolean,
  ): TopicField[] {
    return this.getValue(filterByReadOnly, filterByEmptyValue, filterByVirtual).map(
      (item: FieldConfig & { applyTimeZone?: boolean }) => {
        let value = item.value;
        let timezoneShift: number;

        if (item.type === FieldType.Date) {
          let format = this.dateTimeFields.includes(item.name)
            ? Converter.DateAndTime.FIELDS_DATETIME_FORMAT
            : Converter.DateAndTime.FIELDS_DATE_FORMAT;
          if (value) {
            if (this.isRoomLevel && item.applyTimeZone) {
              timezoneShift = this.timeZoneService.getRoomToDomainZoneShift(
                Converter.DateAndTime.fromCSharpString(value),
              );

              format = Converter.DateAndTime.FIELDS_DATETIME_FORMAT;
            }

            value = moment(Converter.DateAndTime.fromCSharpString(value)).format(format);
          }
        } else if (item.type === FieldType.Number) {
          value = isNaN(+value) ? null : value;
        }

        return {
          FieldName: item.name,
          Fieldvalue: value,
          FieldType: item.type,
          ...(timezoneShift && { TimeZoneShift: timezoneShift }),
        } as TopicField;
      },
    );
  }

  public validate(options: { markInvalidControlAsTouched?: boolean } = {}): boolean {
    if (!this.formGroup) {
      return false;
    }
    this.resetHiddenFieldsValidators();

    const isValid = this.formGroup.valid || this.formGroup.status === 'DISABLED';

    this.validationErrors = Object.keys(this.formGroup.controls)
      .filter((name) => !!this.formGroup.controls[name].errors)
      .map((name) => {
        if (options.markInvalidControlAsTouched) {
          this.formGroup.controls[name].markAsTouched();
        }
        const field = this.fieldsToGenerate.find((topicField) => topicField.fieldName === name);

        return {
          name: `${this.seed}_${name}`,
          title: field ? field.fieldTitle : name,
        };
      });

    return isValid;
  }

  onResize() {
    if (this.isWide !== undefined || !this.formConfig || !this.formConfig.formFields) {
      return;
    }

    this._isWide =
      this.container && this.container.nativeElement && this.container.nativeElement.offsetWidth >= this.criticalWidth;

    this.formConfig.formFields.forEach((fieldConfig: FormFieldConfig) => {
      fieldConfig.UIData = fieldConfig.UIData || {};
      fieldConfig.UIData.isWide = this._isWide;
    });

    // OnPush field components don't re-evaluate their templates on `cdr.detectChanges()` here
    // because the parent CD doesn't bypass OnPush dirty-checks for descendant views. Mutating
    // `config.UIData.isWide` doesn't change the input reference, so they stay clean. Mark each
    // dynamically-rendered child for check explicitly so OnPush ones re-render on the next CD.
    this.rendererComponentsList?.forEach((renderer) => {
      renderer.renderedComponentRef?.injector.get(ChangeDetectorRef).markForCheck();
    });

    this.cdr.detectChanges();
    this.onFormResize.emit(this._isWide);
  }

  private applyConditionalValidations() {
    if (!this.formConfig || !this.formGroup) return;
    this.isTransformingForm = true;

    this.dynamicFieldsService.applyConditionalValidations({
      formGroupRef: this.formGroup,
      formConfig: this.formConfig,
      values: this.value,
      isReadOnly: this.isReadOnly,
      isNewDocument: this.isNewEntity,
      isAlreadyEffectiveDocument: this.isAlreadyEffectiveDocument,
      isChangeControlOptionsEnabled: this.isChangeControlOptionsEnabled,
      isCreateDocument: this.isCreateDocument,
      isEditMode: this.isEditMode,
      isPlaceholder: this.isPlaceholder,
      relatedDynamicForms: this.relatedDynamicForms,
      entity: this.entity,
      viewMode: this.viewMode,
      formSeed: this.formSeed,
      hasESignatureRequest: this.hasESignatureRequest,
    });

    this.isTransformingForm = false;
  }

  public checkReadonly() {
    this.applyConditionalValidations();
  }

  public getFieldCheckerIndex(fieldConfig: FormFieldConfig) {
    if (this.useFormIdForCheckerIndex) {
      return this.formConfig.formId + '_' + fieldConfig.fieldName;
    }

    return fieldConfig.fieldName;
  }

  public removeFieldRequest(fieldName: string) {
    this.onFieldRemoveRequest.emit(fieldName);
  }

  /*
  public getHumanReadableValue(): {
    name: string;
    fieldName: string;
    value: any;
  }[] {
    const value = this.getAllTopicFields(false, false, false).filter(tf => (tf.Fieldvalue !== null) && (tf.Fieldvalue !== undefined));

    const result = value.map(tf => {
      const field = this.fieldsToGenerate.find(ffc => ffc.fieldName === tf.FieldName);

      const cmp = this.componentsHash[tf.FieldName];

      return {
        name: field ? field.fieldTitle || tf.FieldName : tf.FieldName,
        fieldName: field.fieldName,
        value: cmp ? cmp.displayValue || tf.Fieldvalue : tf.Fieldvalue
      };
    });

    return result;
  }
  */

  public updateFieldsToGenerate(fields: FormFieldConfig[]) {
    //workaround for updating facets-panel
    this.fieldsToGenerate = [];
    this.cdr.detectChanges();
    if (
      fields.every((field) => this.fieldsToGenerate.find((fGenerated) => fGenerated.fieldTitle === field.fieldTitle))
    ) {
      return;
    }
    this.fieldsToGenerate = [...fields];
  }

  checkIsPanelExpanded(field: FormFieldConfig) {
    return field.facetedSearchField || this.isPanelbarItemsExpanded;
  }

  getCountSelectedFacets(field: FormFieldConfig) {
    return field.UIData.facetedSearchResult?.AggregationBuckets?.filter((b) => b.isChecked).length;
  }

  clearFieldSelectedFacets(event: MouseEvent, field: FormFieldConfig) {
    event?.stopPropagation();
    field.UIData.facetedSearchResult?.AggregationBuckets.forEach((b) => (b.isChecked ? (b.isChecked = false) : null));
    this.onChange.emit();
  }

  removeField(event: MouseEvent, fieldName: string) {
    event?.stopPropagation();
    this.removeFieldRequest(fieldName);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.isStrictCheckReadOnly
        ? changes.isReadOnly &&
          changes.isReadOnly?.currentValue !== changes.isReadOnly?.previousValue &&
          !changes.isReadOnly?.firstChange
        : changes.isReadOnly?.currentValue
    ) {
      this.rebuildForm();

      this.checkReadonly();
    }
  }

  public getDisplayValuesFromRenderer(): { FieldName: string; DisplayValue: string; FieldType: FieldType }[] {
    return this.rendererComponentsList?.map((item) => ({
      FieldName: item.renderedComponentRef?.instance?.config?.fieldName,
      FieldType: item.renderedComponentRef?.instance?.config?.fieldType,
      DisplayValue: item.renderedComponentRef?.instance?.displayValue,
    }));
  }

  get isAllExpanded(): boolean {
    return !this.sectionComponents
      ?.filter((section) => section.appearance === FormSectionAppearance.Accordion)
      .some?.((section) => section.collapsed);
  }

  get hasAccordionSection(): boolean {
    return some(this.sections, (section: LayoutSection) => section.appearance === FormSectionAppearance.Accordion);
  }

  toggleExpand(isExpanded?: boolean) {
    this.sectionState = mapValues(this.sectionState, () => !isExpanded);
    this.cdr.detectChanges();
  }

  isDuplicateTitleError(field: FormFieldConfig) {
    const titleName = 'TopicName';
    return field.fieldName === titleName && this.formGroup?.controls[titleName]?.hasError('titleExists');
  }

  get hasActiveSection() {
    return this.sections.some((a) => a.active);
  }

  private resolveSiblingDateBoundaryTitles(): void {
    if (!this.fieldsToGenerate?.length) {
      return;
    }
    const titleByName = new Map<string, string>(
      this.fieldsToGenerate.map((f): [string, string] => [f.fieldName, f.fieldTitle]),
    );
    this.fieldsToGenerate.forEach((field) => {
      const uiData = field.UIData;
      if (!uiData) {
        return;
      }
      if (uiData.minDateFromField) {
        uiData.minDateFromFieldTitle = titleByName.get(uiData.minDateFromField);
      }
      if (uiData.maxDateFromField) {
        uiData.maxDateFromFieldTitle = titleByName.get(uiData.maxDateFromField);
      }
    });
  }

  private resetHiddenFieldsValidators() {
    const existedKeys = new Set(this.fieldsToGenerate?.map((f) => f.fieldName));
    forEach(this.formGroup.controls, (control, name) => {
      if (!existedKeys.has(name)) {
        // remove validators from not available fields.
        this.formGroup.controls[name]?.setValidators([]);
        this.formGroup.controls[name]?.updateValueAndValidity();
        this.formGroup.updateValueAndValidity();
      }
    });
  }
}
